import { groupBy } from 'es-toolkit';

import {
  AnalysisHistoryRecord,
  ComponentDesign,
  ComponentDesignLayoutModel,
  ComponentDesignTimestamps,
  Project,
  ProjectChange,
  ProjectSynchronizationPayload,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';

import {
  combineLoadedProjectData,
  createEmptyLoadedData,
  LoadedProjectData,
  migrateIncomingProject,
} from '../loadedData';
import { createChildrenFinder } from '../selectors/ChildrenFinder';
import { getDescendantGuids } from '../selectors/getDescendants';
import {
  AsyncLayoutModelsLibraryThunkAction,
  LayoutModelsLibraryState,
  LayoutModelsLibraryThunkAction,
} from '../store';
import { projectSynchronized } from '../store/layoutModelsSlice';
import { componentDesignToLayoutModel } from './componentDesignActions';

// Determine this value through experimentation. 300 is a good trade-off between loading time and sync service pressure.
const PROJECTS_BATCH_SIZE = 300;

/**
 * Like `loadProjectAndComponentDesignLayoutModels`, but it accepts multiple projects and only loads the component
 * design summaries.
 */
export const loadProjectsAndComponentDesignSummaryLayoutModels =
  (
    projects: readonly Project[],
    options: { firstBatchSize?: number } = {},
  ): LayoutModelsLibraryThunkAction<{
    whenFirstBatchLoaded: Promise<void>;
    whenAllLoaded: Promise<LoadedProjectData>;
  }> =>
  (dispatch, getState) => {
    let i = 0;
    let batchSize = options?.firstBatchSize ?? PROJECTS_BATCH_SIZE;

    const tasks: (() => Promise<LoadedProjectData>)[] = [];

    while (i < projects.length) {
      const projectBatch = projects.slice(i, Math.min(i + batchSize, projects.length));

      tasks.push(async () => {
        let batchData = createEmptyLoadedData();
        const individualProjectDatas: [string, LoadedProjectData][] = [];

        const payload: Record<string, ProjectSynchronizationPayload> = {};

        for (const project of projectBatch) {
          const projectData = migrateIncomingProject(project);

          batchData = combineLoadedProjectData(batchData, projectData);
          individualProjectDatas.push([project.guid, projectData]);
        }

        const componentDesigns = await designService.componentDesignSummaries.fetchBulkWithTimestamps(
          Array.from(batchData.componentDesigns),
        );

        for (const [projectGuid, projectData] of individualProjectDatas) {
          const projectComponentDesignLayoutModelsAndTimestamps = {
            layoutModels: componentDesigns.componentDesignSummaries
              .filter((design) => projectData.componentDesigns.has(design.guid))
              .map(componentDesignToLayoutModel),

            timestamps: componentDesigns.timestamps.filter((timestamps) =>
              projectData.componentDesigns.has(timestamps.componentDesignGuid),
            ),
          };

          payload[projectGuid] = buildProjectSynchronizationPayload(getState(), projectData, projectGuid, {
            componentDesignData: projectComponentDesignLayoutModelsAndTimestamps,
          });
        }

        dispatch(projectSynchronized(payload));

        return batchData;
      });

      i += batchSize;

      // After the first batch is loaded, increase the batch size to load remaining projects efficiently.
      if (i === options?.firstBatchSize) {
        batchSize = PROJECTS_BATCH_SIZE;
      }
    }

    const firstTask = tasks.shift();

    if (!firstTask) {
      return { whenFirstBatchLoaded: Promise.resolve(), whenAllLoaded: Promise.resolve(createEmptyLoadedData()) };
    }

    const firstTaskPromise = firstTask();

    return {
      whenFirstBatchLoaded: firstTaskPromise.then(() => {}),
      whenAllLoaded: new Promise((resolve, reject) => {
        firstTaskPromise
          .then(async (firstTaskData) => {
            let loadedData = firstTaskData;

            while (tasks.length > 0) {
              loadedData = combineLoadedProjectData(loadedData, await tasks.shift()!());
            }

            resolve(loadedData);
          })
          .catch(reject);
      }),
    };
  };

/**
 * Like `loadProjectsAndComponentDesignSummaryLayoutModels`, but it only takes a single project and loads the full
 * component designs, not just the summaries. Note that related data for each component design still needs to be loaded
 * for them to be considered "fully loaded." Primarily geared toward loading a project in Design.
 */
export const loadProjectAndComponentDesignLayoutModels =
  (
    project: Project,
  ): AsyncLayoutModelsLibraryThunkAction<{
    componentDesigns: readonly ComponentDesign[];
    timestamps: readonly ComponentDesignTimestamps[];
    history: readonly AnalysisHistoryRecord[];
  }> =>
  async (dispatch, getState) => {
    const projectData = migrateIncomingProject(project);

    const componentDesignGuids = Array.from(projectData.componentDesigns);
    const [componentDesigns, timestamps, history] = await Promise.all([
      designService.componentDesigns.bulkLoad(componentDesignGuids),
      designService.componentDesigns.bulkLoadTimestamps(componentDesignGuids),
      designService.analysisHistory.bulkLoad(componentDesignGuids),
    ]);

    const componentDesignLayoutModels = componentDesigns.map((componentDesign) =>
      componentDesignToLayoutModel(componentDesign),
    );

    const payload = {
      [project.guid]: buildProjectSynchronizationPayload(getState(), projectData, project.guid, {
        componentDesignData: {
          layoutModels: componentDesignLayoutModels,
          timestamps,
        },
      }),
    };

    dispatch(projectSynchronized(payload));

    return { componentDesigns, timestamps, history };
  };

export function buildProjectSynchronizationPayload(
  state: LayoutModelsLibraryState,
  data: LoadedProjectData,
  projectGuid: string,
  additionalData: {
    componentDesignData: {
      layoutModels: readonly ComponentDesignLayoutModel[];
      timestamps: readonly ComponentDesignTimestamps[];
    };
    changes?: ProjectChange;
  },
) {
  const allLayoutModels = data.models.concat(additionalData.componentDesignData.layoutModels);

  const oldModelGuids = new Set<string>([projectGuid]);

  if (projectGuid in state.layoutModels) {
    const findChildren = createChildrenFinder(
      groupBy(state.insertions, (insertion) => insertion.parentGuid),
      state.layoutModels,
    );
    for (const modelGuid of getDescendantGuids(projectGuid, findChildren)) {
      oldModelGuids.add(modelGuid);
    }
  }

  for (const updatedModel of allLayoutModels) {
    oldModelGuids.add(updatedModel.guid);
  }

  for (const { parentGuid, objectGuid } of data.insertions) {
    oldModelGuids.add(parentGuid);
    oldModelGuids.add(objectGuid);
  }

  return {
    expiredModelGuids: Array.from(oldModelGuids),
    expiredParentGuids: Array.from(oldModelGuids),
    changes: additionalData.changes,
    incomingModels: allLayoutModels,
    incomingInsertions: data.insertions.slice(),
    incomingTimestamps: additionalData.componentDesignData.timestamps,
  };
}

export async function fetchComponentDesignData(componentDesignGuids: readonly string[]) {
  const componentDesigns = await designService.componentDesignSummaries.fetchBulkWithTimestamps(componentDesignGuids);
  const layoutModels = componentDesigns.componentDesignSummaries.map((summary) =>
    componentDesignToLayoutModel(summary),
  );
  return { layoutModels, timestamps: componentDesigns.timestamps };
}
